import React from "react";

const DEFAULT_RANK_STYLE = { backgroundColor: "var(--color-band-bg)", color: "var(--color-brand)" };

// points/onPointsClick render a bottom-right points badge instead of the
// rank badge (used on the Lineup tab header); pass rank/rankStyle for the
// standings-list rank badge instead. Only one badge renders at a time.
const UserAvatar = ({ member, size = 56, rankStyle = DEFAULT_RANK_STYLE, rank, points, onPointsClick }) => {
  const avatarUrl = member.teamAvatar || member.avatar_url;
  const firstName = member.firstName || member.first_name;

  // Handle custom uploaded images (URLs or base64) vs preset avatars
  const isCustomUpload = avatarUrl && (avatarUrl.startsWith('http') || avatarUrl.startsWith('data:'));

  // The ring is drawn inside the box, so at list sizes a fixed 3px spends a
  // sixth of the avatar on white. Thinner below 48 buys back visible logo
  // without changing the footprint the row is laid out against.
  const ringWidth = size < 48 ? 2 : 3;

  // Initials and preset numbers used fixed Tailwind sizes, which read as a
  // speck once the avatar is opened large. Scale with the circle instead.
  const glyphSize = Math.max(12, Math.round(size * 0.42));

  return (
    <div className="relative inline-block">
      <div
        className="rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-sm overflow-hidden"
        style={{
          width: size,
          height: size,
          border: `${ringWidth}px solid var(--color-bg-card)`,
          backgroundColor: rankStyle.backgroundColor,
          color: rankStyle.color
        }}
      >
        {avatarUrl ? (
          isCustomUpload ? (
            // Custom uploaded image (URL or base64)
            <img
              src={avatarUrl}
              alt={`${firstName || "Manager"}'s avatar`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : (
            // Preset numbered avatar
            <div className="w-full h-full font-bold flex items-center justify-center" style={{ backgroundColor: "var(--color-brand)", color: "var(--color-bg-card)", fontSize: glyphSize }}>
              {['avatar1.png', 'avatar2.png', 'avatar3.png', 'avatar4.png', 'avatar5.png', 'avatar6.png', 'avatar7.png', 'avatar8.png'].indexOf(avatarUrl) + 1}
            </div>
          )
        ) : (
          // Fallback to user initials
          <div className="w-full h-full flex items-center justify-center font-bold" style={{ fontSize: glyphSize }}>
            {firstName ? firstName.charAt(0).toUpperCase() : '?'}
          </div>
        )}

        {/* Fallback initials (hidden by default, shown if image fails) */}
        <div
          className="w-full h-full flex items-center justify-center font-bold"
          style={{ display: 'none', fontSize: glyphSize }}
        >
          {firstName ? firstName.charAt(0).toUpperCase() : '?'}
        </div>
      </div>

      {/* Points Badge (Lineup tab header) */}
      {points != null && (
        <button
          onClick={onPointsClick}
          disabled={!onPointsClick}
          className="absolute -bottom-2 -right-2 rounded-full flex flex-col items-center justify-center shadow-md transition-transform active:scale-95"
          style={{
            width: Math.max(44, size * 0.5),
            height: Math.max(44, size * 0.5),
            backgroundColor: "var(--color-brand)",
            border: "3px solid var(--color-bg-card)",
            cursor: onPointsClick ? "pointer" : "default",
          }}
        >
          <span className="font-bold leading-none text-white" style={{ fontSize: size * 0.15 }}>
            {points >= 1000 ? `${(points / 1000).toFixed(1)}k` : points.toLocaleString()}
          </span>
          <span className="leading-none text-white opacity-70" style={{ fontSize: 8, marginTop: 2 }}>
            PTS
          </span>
        </button>
      )}

      {/* Rank Number Badge */}
      {rank != null && points == null && (
        <div
          className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-md border-2 border-white"
          style={{
            backgroundColor: rankStyle.backgroundColor,
            color: rankStyle.color
          }}
        >
          {rank}
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
